import {
  OutlinedInput,
  Select,
  MenuItem,
  SelectChangeEvent,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import CasControl from '../CasControl/CasControl';


/*
  CasSelect intentionally does not support multiple props. It is for a single value.
  (If we need multiple=true in the future, we'll implement a separate component wrapper.)
*/

export interface ICasSelect {
  value: string;
  // null when the caller renders its own label - see CasControl.
  label: string | null;
  onChange: (event: SelectChangeEvent<any>, value: string) => void;
  placeholder?: string;
  name?: string;
  options: any[];
  optionsNameField?: string;
  optionsIdField?: string;
  emptyMessage?: string;
  sx?: SxProps<Theme>;
  className?: string;
  /*
    'display' renders the selected option's NAME as plain text instead of an
    input. Lets read-only surfaces (e.g. CasTable's filter chips) resolve an id
    to its label by reusing the select's own option list — no duplicate lookup.
  */
  mode?: 'input' | 'display';
  // Stretch the control (and its CasControl wrapper) to the container width —
  // used by the CasTable filter drawer, where there is no toolbar row to keep tight.
  fullWidth?: boolean;
}

/*
  MUI - like React - reads a nullish value as "this input is uncontrolled". Table filters start
  out null and are given a value a render later, so the input flipped from uncontrolled to
  controlled mid-life and React logged a warning on every CasTable with a select filter.

  null cannot simply be coalesced away, because it is a REAL option value here: prependAllOption
  and prependNoneOption both emit { id: null }. So null is mapped to '' - MUI's own "nothing
  selected" sentinel, and a defined value - on the way in, for the selected value AND for the
  options, so an "All" / "(none)" entry still matches and still displays.

  '' is only mapped BACK to null when the options actually offer a null id. Without that guard a
  list holding a genuine ''-valued option (CasMRTableFilter builds its options from the data, so
  a column with blank cells produces one) would start reporting null for it. A list containing
  BOTH a null option and a ''-valued option still cannot tell them apart - they are the same
  thing to MUI - but that pairing is meaningless anyway and was already broken: '' is falsy, so
  React read the old key={option.id} as no key at all.
*/
const NO_SELECTION = '';

const toSelectValue = (value: any) => (value === null || value === undefined ? NO_SELECTION : value);

const CasSelect = ({
  className,
  label,
  value,
  onChange,
  placeholder,
  name,
  options = [],
  optionsNameField = 'name',
  optionsIdField = 'id',
  emptyMessage,
  sx,
  mode = 'input',
  fullWidth,
}: ICasSelect) => {
  if (mode === 'display') {
    const selected = options.find((option: any) => option[optionsIdField] === value);
    return <>{selected?.[optionsNameField] ?? ''}</>;
  }

  if (emptyMessage && options.length === 0) {
    return (
      <Typography color="#637381" fontStyle="italic">
        {emptyMessage}
      </Typography>
    );
  }

  const hasNullOption = options.some((option: any) => option[optionsIdField] === null);

  const fromSelectValue = (selected: any) =>
    hasNullOption && selected === NO_SELECTION ? null : selected;

  return (
    // shrink deliberately reads the ORIGINAL value, not the mapped one: null still means "a
    // filter is present but unset", which floats the label exactly as it did before.
    <CasControl label={label} shrink={value !== undefined && value !== "" } fullWidth={fullWidth}>
      <Select
        displayEmpty={hasNullOption}   // allow treating null option as value when null option is provided.
        className={className}
        value={toSelectValue(value)}
        name={name}
        onChange={(event) => onChange(event, fromSelectValue(event.target.value))}
        input={<OutlinedInput label={placeholder} />}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: 240 },
          },
        }}
        sx={sx}
      >
        {options.map((option: any) => (
          // String() so the All / (none) entry's null id still yields a usable key - React reads
          // a null key as no key at all.
          <MenuItem key={String(option[optionsIdField])} value={toSelectValue(option[optionsIdField])}>
            {option[optionsNameField]}
          </MenuItem>
        ))}
      </Select>
    </CasControl>
  );
};

export default CasSelect;
